import { useMutation, useQuery } from '@tanstack/react-query'
import { useUserData } from 'auth'
import { useOrgId } from 'helpers'
import type { MutableRefObject } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTRPC } from 'utils/trpc'
import { useLatestRef, waitForSelector } from './constants'
import { getOnboardingTourSteps, routeMatches, type OnboardingTourStep, type TourStepKey } from './tours'

const ONBOARDING_STEPS = [
    { key: 'logo', label: 'Upload Your Logo' },
    { key: 'xero', label: 'Connect Xero' },
    { key: 'commission', label: 'Set Your Engagement Fee' },
    { key: 'wishlist', label: 'Try Your Wishlist' },
    { key: 'form6', label: 'Send a Form 6' },
    { key: 'invoices', label: 'How Invoicing Works' },
] as const

export type OnboardingStepKey = (typeof ONBOARDING_STEPS)[number]['key']

export type OnboardingProgress = {
    orgDetailsDone: boolean
    xeroDone: boolean
    xeroConnected: boolean
    xeroConfigured: boolean
    commissionDone: boolean
    engagementFee: number | null
    hasPurchaser: boolean
    hasWishlistFilled: boolean
    hasForm6Created: boolean
    hasForm6Sent: boolean
    invoiceStepSeen: boolean
    completed: boolean
    currentStep?: number
    firstPurchaserId?: string
}

interface OnboardingContextType {
    progress: OnboardingProgress | undefined
    isLoading: boolean
    currentStep: number
    setCurrentStep: (step: number) => void
    expanded: boolean
    setExpanded: (expanded: boolean) => void
    tourActive: boolean
    setTourActive: (active: boolean) => void
    steps: typeof ONBOARDING_STEPS
    completedCount: number
    completeOnboarding: () => void
    markInvoiceStepSeen: () => void
    refetchProgress: () => Promise<any>
    isStepComplete: (index: number) => boolean
    // Step flow state
    isInTourStepFlow: boolean
    tourSteps: OnboardingTourStep[]
    tourCurrentStepIndex: number
    tourHighlights: { tourId: string }[]
    startStepTour: (stepKey: OnboardingStepKey | TourStepKey) => void
    skipTourStep: () => void
    exitTourStepFlow: () => void
    tourGenerationRef: MutableRefObject<number>
    clearTourHighlights: () => void
    testPurchaserId: string | null
    setTestPurchaserId: (id: string | null) => void
}

const OnboardingCtx = createContext<OnboardingContextType | null>(null)
export const useOnboarding = () => {
    const ctx = useContext(OnboardingCtx)
    if (!ctx) throw new Error('useOnboarding must be used within OnboardingProvider')
    return ctx
}
export const useOptionalOnboarding = () => useContext(OnboardingCtx)

const COLLAPSE_KEY = 'onboarding-collapsed'

export const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
    const trpc = useTRPC()
    const { refreshUser } = useUserData()
    const location = useLocation()
    const orgId = useOrgId()

    const progressQuery = useQuery(trpc.onboarding.getProgress.queryOptions())
    const updateMutation = useMutation(trpc.onboarding.updateProgress.mutationOptions())

    const progress = progressQuery.data

    const { user } = useUserData()
    const hasLogo = !!user.orgLogo

    // Determine first incomplete step for initial position
    const getFirstIncompleteStep = useCallback(
        (p: OnboardingProgress | undefined): number => {
            if (!p) return 0
            if (!hasLogo) return 0
            if (!p.xeroDone) return 1
            if (!p.commissionDone) return 2
            if (!p.hasWishlistFilled) return 3
            if (!p.hasForm6Sent) return 4
            if (!p.invoiceStepSeen) return 5
            return 5
        },
        [hasLogo],
    )

    const [currentStep, setCurrentStepRaw] = useState<number>(0)
    const [expanded, setExpandedRaw] = useState<boolean>(() => localStorage.getItem(COLLAPSE_KEY) !== 'true')
    const [tourActive, setTourActive] = useState(false)
    const [initialized, setInitialized] = useState(false)
    const [testPurchaserId, setTestPurchaserId] = useState<string | null>(null)

    // Step flow state
    const [tourSteps, setTourSteps] = useState<OnboardingTourStep[]>([])
    const [tourCurrentStepIndex, setTourCurrentStepIndex] = useState(-1)
    const [tourHighlights, setTourHighlights] = useState<{ tourId: string }[]>([])
    const [minStepIndex, setMinStepIndex] = useState(0)
    const tourStepsRef = useLatestRef(tourSteps)
    const minStepIndexRef = useLatestRef(minStepIndex)
    const tourActiveRef = useLatestRef(tourActive)
    const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
    const tourGenerationRef = useRef(0)
    const stepWasCompleteOnLaunchRef = useRef(false)

    const isInTourStepFlow = tourCurrentStepIndex >= 0 && tourSteps.length > 0

    // Initialize step from progress data on first load
    useEffect(() => {
        if (progress && !initialized) {
            // If returning from wishlist submission, force the wishlist step
            const params = new URLSearchParams(window.location.search)
            if (params.get('from') === 'wishlist') {
                setCurrentStepRaw(3)
                setInitialized(true)
                return
            }
            const savedStep = progress.currentStep
            const firstIncomplete = getFirstIncompleteStep(progress)
            setCurrentStepRaw(savedStep ?? firstIncomplete)
            setInitialized(true)
        }
    }, [progress, initialized, getFirstIncompleteStep])

    const stepSaveRef = useRef<ReturnType<typeof setTimeout>>(undefined)
    const setCurrentStep = useCallback(
        (step: number) => {
            setCurrentStepRaw(step)
            clearTimeout(stepSaveRef.current)
            stepSaveRef.current = setTimeout(() => {
                updateMutation.mutate({ currentStep: step })
            }, 1000)
        },
        [updateMutation],
    )

    const setExpanded = useCallback((val: boolean) => {
        setExpandedRaw(val)
        localStorage.setItem(COLLAPSE_KEY, val ? 'false' : 'true')
    }, [])

    const isStepComplete = useCallback(
        (index: number): boolean => {
            if (!progress) return false
            switch (index) {
                case 0:
                    return hasLogo
                case 1:
                    return progress.xeroDone
                case 2:
                    return progress.commissionDone
                case 3:
                    return progress.hasWishlistFilled
                case 4:
                    return progress.hasForm6Sent
                case 5:
                    return progress.invoiceStepSeen
                default:
                    return false
            }
        },
        [progress, hasLogo],
    )

    const completedCount = useMemo(
        () =>
            progress
                ? [
                      hasLogo,
                      progress.xeroDone,
                      progress.commissionDone,
                      progress.hasWishlistFilled,
                      progress.hasForm6Sent,
                      progress.invoiceStepSeen,
                  ].filter(Boolean).length
                : 0,
        [progress, hasLogo],
    )

    const completeOnboarding = useCallback(async () => {
        await updateMutation.mutateAsync({ completed: true, invoiceStepSeen: true })
        await refreshUser()
    }, [updateMutation, refreshUser])

    const markInvoiceStepSeen = useCallback(async () => {
        await updateMutation.mutateAsync({ invoiceStepSeen: true })
        await progressQuery.refetch()
    }, [updateMutation, progressQuery])

    const refetchProgress = useCallback(() => {
        return progressQuery.refetch()
    }, [progressQuery])

    // ── Step flow actions ──────────────────────────────────────

    const clearTourHighlights = useCallback(() => {
        setTourHighlights([])
    }, [])

    const exitTourStepFlow = useCallback(() => {
        setTourCurrentStepIndex(-1)
        setTourSteps([])
        setTourHighlights([])
        setTourActive(false)
        setMinStepIndex(0)
        if (pollRef.current) clearInterval(pollRef.current)
        refetchProgress()
    }, [refetchProgress])

    // Reactive sync: find the first non-done step from minStepIndex
    const syncTourStep = useCallback(() => {
        const allSteps = tourStepsRef.current
        if (!allSteps.length || !tourActiveRef.current) return

        const min = minStepIndexRef.current
        const loc = { pathname: window.location.pathname, search: window.location.search }

        for (let i = min; i < allSteps.length; i++) {
            const step = allSteps[i]

            // Check doneWhenRoute
            if (step.doneWhenRoute && routeMatches(step.doneWhenRoute, loc, step.doneWhenRouteDefaults))
                continue

            // Check doneWhenMissing
            if (step.doneWhenMissing && !document.querySelector(`[data-tour="${step.doneWhenMissing}"]`))
                continue

            // Check doneWhenPresent
            if (step.doneWhenPresent && document.querySelector(`[data-tour="${step.doneWhenPresent}"]`))
                continue

            // This step is active
            setTourCurrentStepIndex(i)
            if (step.tourId) {
                const el = document.querySelector(`[data-tour="${step.tourId}"]`)
                if (el) {
                    setTourHighlights([{ tourId: step.tourId }])
                } else {
                    setTourHighlights([])
                    waitForSelector(`[data-tour="${step.tourId}"]`).then(found => {
                        if (found && tourActiveRef.current) {
                            setTourHighlights([{ tourId: step.tourId! }])
                        }
                    })
                }
            } else {
                setTourHighlights([])
            }
            return
        }

        // All steps done — complete tour
        exitTourStepFlow()
    }, [exitTourStepFlow])

    // Skip / Next: bump minStepIndex past current step, then sync
    const skipTourStep = useCallback(() => {
        setMinStepIndex(prev => {
            const next = Math.max(prev, tourCurrentStepIndex + 1)
            minStepIndexRef.current = next
            return next
        })
        // Sync after state update — refs are already set, rAF ensures DOM is ready
        requestAnimationFrame(syncTourStep)
    }, [tourCurrentStepIndex, syncTourStep])

    const launchTourSteps = useCallback(
        (stepKey: OnboardingStepKey | TourStepKey) => {
            const tourKey = stepKey as TourStepKey
            const steps = getOnboardingTourSteps(tourKey, orgId!, {
                xeroConnected: progress?.xeroConnected,
                firstPurchaserId: progress?.firstPurchaserId,
                userId: user.userId,
            })

            tourGenerationRef.current++
            stepWasCompleteOnLaunchRef.current = isStepComplete(currentStep)
            setTourSteps(steps)
            tourStepsRef.current = steps
            setMinStepIndex(0)
            minStepIndexRef.current = 0
            setTourActive(true)
            tourActiveRef.current = true

            // Run sync after state is set — refs are already set, rAF ensures DOM is ready
            requestAnimationFrame(syncTourStep)
        },
        [orgId, progress, user.userId, syncTourStep, isStepComplete, currentStep],
    )

    const startStepTour = useCallback(
        (stepKey: OnboardingStepKey | TourStepKey) => {
            if (stepKey === 'invoices') return

            // Don't auto-navigate — let the tour guide the user to click through
            launchTourSteps(stepKey)
        },
        [launchTourSteps],
    )

    // Re-sync on location changes
    useEffect(() => {
        if (tourActive) {
            // Small delay for DOM to update after navigation
            const timeout = setTimeout(syncTourStep, 100)
            return () => clearTimeout(timeout)
        }
    }, [location.pathname, location.search, tourActive, syncTourStep])

    // MutationObserver to re-sync on DOM changes (debounced)
    useEffect(() => {
        if (!tourActive) return

        let debounceTimer: ReturnType<typeof setTimeout>
        const observer = new MutationObserver(() => {
            clearTimeout(debounceTimer)
            debounceTimer = setTimeout(syncTourStep, 300)
        })

        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
            clearTimeout(debounceTimer)
        }
    }, [tourActive, syncTourStep])

    // Poll progress while tour is active
    useEffect(() => {
        if (tourActive) {
            pollRef.current = setInterval(() => refetchProgress(), 5000)
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current)
        }
    }, [tourActive, refetchProgress])

    // Stable ref for skipTourStep so the autoAdvance effect doesn't re-run when it changes
    const skipTourStepRef = useRef(skipTourStep)
    skipTourStepRef.current = skipTourStep

    // Auto-advance after a delay when the current step has autoAdvanceMs (triggered by user interaction)
    useEffect(() => {
        const step = tourSteps[tourCurrentStepIndex]
        if (!tourActive || tourCurrentStepIndex < 0 || !step?.autoAdvanceMs || !step.tourId) return

        const container = document.querySelector(`[data-tour="${step.tourId}"]`)
        if (!container) return

        let timeout: ReturnType<typeof setTimeout> | null = null

        // Use native input event — fires reliably when users type, unlike
        // MutationObserver which misses React-controlled value property changes.
        const handleInput = () => {
            if (timeout) clearTimeout(timeout)
            timeout = setTimeout(() => {
                skipTourStepRef.current()
            }, step.autoAdvanceMs!)
        }

        container.addEventListener('input', handleInput, true)

        return () => {
            container.removeEventListener('input', handleInput, true)
            if (timeout) clearTimeout(timeout)
        }
    }, [tourActive, tourCurrentStepIndex, tourSteps])

    // Auto-exit tour when the current step becomes complete (but not if it was already complete when launched)
    useEffect(() => {
        if (tourActive && progress && isStepComplete(currentStep) && !stepWasCompleteOnLaunchRef.current) {
            exitTourStepFlow()
        }
    }, [tourActive, progress, currentStep, isStepComplete, exitTourStepFlow])

    // Escape key to exit step flow
    useEffect(() => {
        if (!tourActive) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') exitTourStepFlow()
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [tourActive, exitTourStepFlow])

    return (
        <OnboardingCtx.Provider
            value={{
                progress,
                isLoading: progressQuery.isLoading,
                currentStep,
                setCurrentStep,
                expanded,
                setExpanded,
                tourActive,
                setTourActive,
                steps: ONBOARDING_STEPS,
                completedCount,
                completeOnboarding,
                markInvoiceStepSeen,
                refetchProgress,
                isStepComplete,
                // Step flow
                isInTourStepFlow,
                tourSteps,
                tourCurrentStepIndex,
                tourHighlights,
                startStepTour,
                skipTourStep,
                exitTourStepFlow,
                tourGenerationRef,
                clearTourHighlights,
                testPurchaserId,
                setTestPurchaserId,
            }}
        >
            {children}
        </OnboardingCtx.Provider>
    )
}
